import React from 'react';
import { Table, Tag } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ActivityLog } from './ActivityLog.types';
import { DEPARTMENT_CONFIG, USER_TYPE_CONFIG, STATUS_CONFIG, formatDateTime, getIcon } from './ActivityLogSampleData';

interface Props {
  logs: ActivityLog[];
  total: number;
  loading: boolean;
  page: number;
  pageSize: number;
  onPageChange: (p: number) => void;
  onSelect: (log: ActivityLog) => void;
}

const ActivityLogTable: React.FC<Props> = ({ logs, total, loading, page, pageSize, onPageChange, onSelect }) => {
  const getDeptCfg = (dept: ActivityLog['department']) =>
    DEPARTMENT_CONFIG[dept] ?? { iconKey: 'export', color: '#533AB7', bg: '#EEEDFE', label: dept };

  const getUserCfg = (ut: ActivityLog['userType']) =>
    USER_TYPE_CONFIG[ut] ?? { iconKey: 'user', color: '#185FA5', bg: '#E6F1FB', label: ut };

  const columns: ColumnsType<ActivityLog> = [
    {
      title: 'Date / Time',
      key: 'timestamp',
      width: 130,
      render: (_, record) => {
        const { date, time } = formatDateTime(record.timestamp);
        return (
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: '#333' }}>{date}</div>
            <div style={{ fontSize: 12, color: '#aaa' }}>{time}</div>
          </div>
        );
      },
    },
    {
      title: 'Department',
      key: 'department',
      width: 165,
      render: (_, record) => {
        const cfg = getDeptCfg(record.department);
        return (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: cfg.bg,
              color: cfg.color,
              borderRadius: 7,
              padding: '4px 10px',
              fontSize: 12,
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            {getIcon(cfg.iconKey, { fontSize: 13 })}
            {cfg.label}
          </span>
        );
      },
    },
    {
      title: 'User',
      key: 'user',
      width: 180,
      render: (_, record) => {
        const cfg = getUserCfg(record.userType);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                background: cfg.bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {getIcon(cfg.iconKey, { fontSize: 14, color: cfg.color })}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#333', lineHeight: 1.3 }}>{record.userName}</div>
              <div style={{ fontSize: 11, color: cfg.color, fontWeight: 500 }}>{cfg.label}</div>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e', marginBottom: 2 }}>{record.action}</div>
          <div
            style={{
              fontSize: 12,
              color: '#888',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 340,
            }}
          >
            {record.description}
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 90,
      align: 'center' as const,
      render: (_, record) => {
        const cfg = STATUS_CONFIG[record.status];
        return (
          <Tag
            style={{
              background: cfg.bg,
              color: cfg.color,
              border: 'none',
              fontWeight: 500,
              borderRadius: 6,
              fontSize: 11,
            }}
          >
            {cfg.label}
          </Tag>
        );
      },
    },
    {
      title: '',
      key: 'eye',
      width: 48,
      align: 'center' as const,
      render: (_, record) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSelect(record);
          }}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#bbb',
            fontSize: 16,
            padding: '4px 6px',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            transition: 'color 0.15s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#555')}
          onMouseLeave={(e) => (e.currentTarget.style.color = '#bbb')}
          aria-label="View log details"
        >
          <EyeOutlined />
        </button>
      ),
    },
  ];

  return (
    <>
      <style>{`
        .alt-row td { background: #fafafa !important; }
        .ant-table-row:hover td { background: #f0f4ff !important; }
      `}</style>
      <Table
        dataSource={logs}
        columns={columns}
        rowKey="id"
        size="middle"
        loading={loading}
        onRow={(record) => ({ onClick: () => onSelect(record), style: { cursor: 'pointer' } })}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          onChange: onPageChange,
          showSizeChanger: false,
          showTotal: (t, range) => `${range[0]}–${range[1]} of ${t}`,
        }}
        style={{ background: '#fff', borderRadius: 12, overflow: 'hidden' }}
        rowClassName={(_, index) => (index % 2 === 0 ? '' : 'alt-row')}
      />
    </>
  );
};

export default ActivityLogTable;
